import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface WishBookItem {
  id: number
  member_id: number
  member_name: string | null
  login_id: string | null
  book_title: string
  affiliation_name: string | null
  is_gifted: number
  created_at: string
}

export default function AdminWishBookList() {
  const [wishBooks, setWishBooks] = useState<WishBookItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; item: WishBookItem | null }>({ open: false, item: null })
  const [resultModal, setResultModal] = useState({ open: false, title: '' })

  const fetchWishBooks = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      if (search) params.set('search', search)
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/admin/wish-book/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const json = await res.json()
      setWishBooks(json.data.wish_books)
      setTotal(json.data.total)
    } catch {
      setResultModal({ open: true, title: '목록을 불러오는 데 실패했습니다.' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWishBooks()
  }, [page, search])

  const handleSearch = () => {
    setPage(1)
    setSearch(searchInput.trim())
  }

  const handleGiftClick = (item: WishBookItem) => {
    setConfirmModal({ open: true, item })
  }

  const handleGiftConfirm = async () => {
    if (!confirmModal.item) return
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/admin/wish-book/${confirmModal.item.id}/gift`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err?.error?.message || '처리 실패')
      }
      setResultModal({ open: true, title: '선물완료 처리되었습니다.' })
      fetchWishBooks()
    } catch (e: any) {
      setResultModal({ open: true, title: e.message ?? '선물완료 처리에 실패했습니다.' })
    } finally {
      setConfirmModal({ open: false, item: null })
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-6">
      <div className="max-w-[1200px] mx-auto">

        {/* 헤더 */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">희망도서 관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
            </p>
          </div>
        </div>

        {/* 검색 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="회원명 또는 희망도서명 검색"
              className="w-72 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]"
            />
            <button
              onClick={handleSearch}
              className="h-9 px-5 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors"
            >
              조회
            </button>
          </div>
        </div>

        {/* 테이블 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">회원명</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">회원 아이디</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">희망도서명</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">소속</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">등록일</th>
                <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">처리</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
              ) : wishBooks.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-300 text-[13px]">등록된 희망도서가 없습니다.</td></tr>
              ) : wishBooks.map(item => (
                <tr key={item.id} className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors">
                  <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">{item.member_name ?? '-'}</td>
                  <td className="px-4 py-3 text-[12px] text-gray-400 font-mono">{item.login_id ?? '-'}</td>
                  <td className="px-4 py-3 text-[13px] text-[#2a1f1a]">{item.book_title}</td>
                  <td className="px-4 py-3 text-[12px] text-gray-400">{item.affiliation_name ?? '-'}</td>
                  <td className="px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">{formatDate(item.created_at)}</td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {item.is_gifted === 1 ? (
                      <span className="text-[11px] px-2 py-0.5 rounded font-medium bg-green-50 text-green-600">선물완료</span>
                    ) : (
                      <button
                        onClick={() => handleGiftClick(item)}
                        className="px-3 py-1 text-[12px] border border-[#887267] text-[#887267] rounded-lg hover:bg-[#f5f2f0] transition-colors"
                      >
                        선물하기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]"
            >
              이전
            </button>
            <span className="text-[13px] text-gray-500">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* 선물완료 확인 모달 */}
      {confirmModal.open && confirmModal.item && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
            <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">선물완료 처리하시겠습니까?</h3>
            <p className="text-[13px] text-gray-400 mb-5">
              {confirmModal.item.member_name}님의 "{confirmModal.item.book_title}" 희망도서를 선물완료 처리합니다.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmModal({ open: false, item: null })}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]"
              >
                취소
              </button>
              <button
                onClick={handleGiftConfirm}
                className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 결과 모달 */}
      {resultModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
            <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{resultModal.title}</p>
            <button
              onClick={() => setResultModal({ open: false, title: '' })}
              className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  )
}